<template>
  <v-dialog persistent max-width="480px" :value="show">
    <v-card :loading="isLoading">
      <v-card-title class="ma-0 pa-6 pb-2 flex-column align-start">
        <p class="ma-0 pa-0 h6 font-weight-600">
          {{ $t('routines.approval.request_approval') }}
        </p>
      </v-card-title>

      <v-card-text class="pa-6 pt-2">
        <div class="d-flex justify-space-between align-center mb-6">
          <AssigneesSearchPopover
            data-testid="routineApprovalRequestDialog-popover-approvers"
            multiple
            :blacklist="blacklist"
            :disabled="!canSelectApprovers"
            :maxSelectableAccounts="remainingRequests"
            :selectedAccountIds="selectedApproverIds"
            @change="handleApproverSelection"
          >
            <Button
              color="primary"
              data-testid="routineApprovalRequestDialog-button-selectApprovers"
              icon="mdi-account-plus-outline"
              outlined
              size="small"
              :disabled="!canSelectApprovers"
              :text="$t('routines.approval.select_approvers')"
            />
          </AssigneesSearchPopover>

          <p
            class="ma-0 mt-2 pa-0 caption font-weight-medium blue-grey--text text--darken-1"
            data-testid="routineApprovalRequestDialog-text-remainingRequests"
          >
            {{
              $t('routines.approval.requests_left', {
                count: remainingRequestsAfterSelection,
              })
            }}
          </p>
        </div>

        <EmptyState
          v-if="!hasSelectedTargets"
          class="mt-4"
          data-testid="routineApprovalRequestDialog-emptyState-noApprovers"
          icon="mdi-account-multiple-check-outline"
          :message="$t('routines.approval.select_approvers_to_request')"
          :title="$t('routines.approval.no_approvers_selected')"
        />

        <div
          v-for="(target, index) in requestTargets"
          :key="target.accountId"
          class="d-flex align-center justify-space-between mt-3"
          :class="{ 'border-bottom': index < requestTargets.length - 1 }"
          data-testid="routineApprovalRequestDialog-row-requestTarget"
        >
          <div
            :data-testid="
              `routineApprovalRequestDialog-container-targetLeft-${target.accountId}`
            "
          >
            <ChipAvatar
              disablePopover
              :account="target.account"
              :vuetifyProps="{ close: isRequestTargetRemovable(target) }"
              @click:close="handleRemoveReadyTarget(target.accountId)"
            />
          </div>

          <div
            class="text-right"
            :data-testid="getTargetContainerRightTestId(target)"
          >
            <v-progress-circular
              v-if="target.status === REQUEST_TARGET_STATUS.LOADING"
              :data-testid="getTargetStatusLoaderTestId(target)"
              indeterminate
              :size="16"
              :width="2"
              color="primary"
            />

            <v-icon
              v-else-if="target.status === REQUEST_TARGET_STATUS.SUCCESS"
              :data-testid="getTargetStatusIconTestId(target)"
              color="green"
              small
            >
              mdi-check
            </v-icon>

            <p
              v-else
              class="mb-0 caption font-weight-medium"
              :class="getTargetStatusClass(target)"
            >
              <span :data-testid="getTargetStatusTestId(target)">
                {{ getTargetStatusCopy(target) }}
              </span>
            </p>

            <p
              v-if="shouldShowTargetError(target)"
              class="mb-0 mt-1 caption red--text"
              :data-testid="getTargetErrorTestId(target)"
            >
              {{ target.errorMessage }}
            </p>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="pa-6 pt-0 justify-end">
        <Button
          color="grey"
          data-testid="routineApprovalRequestDialog-button-cancel"
          plain
          :text="$t('common.button.cancel')"
          @click="$emit('close')"
        />

        <Button
          v-if="isRetryActionMode"
          class="me-0"
          color="primary"
          data-testid="routineApprovalRequestDialog-button-retryFailed"
          :loading="isLoading"
          :text="$t('common.retry')"
          @click="handleRetryFailed"
        />

        <Button
          v-else
          class="me-0"
          color="primary"
          data-testid="routineApprovalRequestDialog-button-save"
          :disabled="!canSubmitRequests"
          :loading="isLoading"
          :text="$t('routines.approval.request_approval')"
          @click="handleSubmit"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'

import Button from '@/components/UI/Button'
import AssigneesSearchPopover from '@/components/AssigneesSearchPopover/AssigneesSearchPopover.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import EmptyState from '@/components/EmptyState.vue'

import {
  REQUEST_TARGET_STATUS,
  REQUEST_TARGET_STATUS_GROUPS,
  MAIN_ACTION_MODE
} from './constants'

export default {
  name: 'RoutineApprovalRequestDialog',

  components: {
    AssigneesSearchPopover,
    Button,
    ChipAvatar,
    EmptyState,
  },

  mixins: [mixin],

  props: {
    blacklist: {
      type: Array,
      default: () => [],
    },
    remainingRequests: {
      type: Number,
      required: true,
    },
    show: Boolean,
    routineId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      REQUEST_TARGET_STATUS,
      REQUEST_TARGET_STATUS_GROUPS,
      MAIN_ACTION_MODE,
      isLoading: false,
      requestTargets: [],
    }
  },

  computed: {
    ...mapGetters(['organizationId']),

    canSelectApprovers() {
      return this.remainingRequestsAfterSelection > 0
    },

    remainingRequestsAfterSelection() {
      return Math.max(0, this.remainingRequests - this.requestTargets.length)
    },

    canSubmitRequests() {
      const actionableCount = this.requestTargets.filter(target =>
        REQUEST_TARGET_STATUS_GROUPS.ACTIONABLE.includes(target.status)
      ).length

      return actionableCount > 0 && actionableCount <= this.remainingRequests
    },

    hasSelectedTargets() {
      return !_.isEmpty(this.requestTargets)
    },

    hasFailedRequests() {
      return this.requestTargets.some(
        target => target.status === REQUEST_TARGET_STATUS.ERROR
      )
    },

    hasPendingRequests() {
      return this.requestTargets.some(target =>
        REQUEST_TARGET_STATUS_GROUPS.PENDING.includes(target.status)
      )
    },

    mainActionMode() {
      if (this.hasFailedRequests) {
        return MAIN_ACTION_MODE.RETRY
      }

      return MAIN_ACTION_MODE.REQUEST
    },

    isRetryActionMode() {
      return this.mainActionMode === MAIN_ACTION_MODE.RETRY
    },

    selectedApproverIds() {
      return this.requestTargets
        .filter(target =>
          REQUEST_TARGET_STATUS_GROUPS.TRACKED.includes(target.status)
        )
        .map(target => target.accountId)
    },
  },

  methods: {
    getTargetName(target) {
      return target.account && (target.account.title || target.account.name)
    },

    getTargetStatusCopy(target) {
      if (target.status === REQUEST_TARGET_STATUS.ERROR) {
        return this.$t('routines.approval.request_status_error')
      }

      if (target.status === REQUEST_TARGET_STATUS.SUCCESS) {
        return this.$t('routines.approval.request_status_requested')
      }

      if (target.status === REQUEST_TARGET_STATUS.LOADING) {
        return this.$t('common.loading')
      }

      return this.$t('routines.approval.request_status_ready_for_request')
    },

    getTargetStatusClass(target) {
      if (target.status === REQUEST_TARGET_STATUS.ERROR) {
        return 'red--text'
      }

      if (target.status === REQUEST_TARGET_STATUS.SUCCESS) {
        return 'green--text'
      }

      return 'blue-grey--text'
    },

    getTargetStatusTestId(target) {
      return `routineApprovalRequestDialog-text-status-${target.accountId}`
    },

    getTargetStatusLoaderTestId(target) {
      return `routineApprovalRequestDialog-loader-status-${target.accountId}`
    },

    getTargetStatusIconTestId(target) {
      return `routineApprovalRequestDialog-icon-status-${target.accountId}`
    },

    getTargetContainerRightTestId(target) {
      return `routineApprovalRequestDialog-container-targetRight-${target.accountId}`
    },

    getTargetErrorTestId(target) {
      return `routineApprovalRequestDialog-text-error-${target.accountId}`
    },

    shouldShowTargetError(target) {
      return (
        target.status === REQUEST_TARGET_STATUS.ERROR &&
        Boolean(target.errorMessage)
      )
    },

    isRequestTargetRemovable(target) {
      return target.status === REQUEST_TARGET_STATUS.READY
    },

    handleApproverSelection(selection) {
      const selectedIds = selection?.ids || []
      const itemsById = _.keyBy(selection?.items || [], 'id')

      this.requestTargets = selectedIds
        .map(accountId => {
          const existingTarget = this.requestTargets.find(
            target => target.accountId === accountId
          )

          if (existingTarget) {
            const updatedAccount = itemsById[accountId]

            return updatedAccount
              ? {
                  ...existingTarget,
                  account: updatedAccount,
                }
              : existingTarget
          }

          const account = itemsById[accountId]

          return {
            account: account || { id: accountId, _id: accountId },
            accountId,
            errorMessage: '',
            status: REQUEST_TARGET_STATUS.READY,
          }
        })
        .filter(Boolean)
    },

    handleRemoveReadyTarget(accountId) {
      this.requestTargets = this.requestTargets.filter(target => {
        if (target.accountId !== accountId) {
          return true
        }

        return target.status !== REQUEST_TARGET_STATUS.READY
      })
    },

    async handleRetryFailed() {
      this.requestTargets = this.requestTargets.map(target => {
        if (target.status !== REQUEST_TARGET_STATUS.ERROR) {
          return target
        }

        return {
          ...target,
          errorMessage: '',
          status: REQUEST_TARGET_STATUS.READY,
        }
      })

      await this.handleSubmit()
    },

    async handleSubmit() {
      if (!this.canSubmitRequests) {
        return
      }

      this.isLoading = true

      const readyTargets = this.requestTargets.filter(
        target => target.status === REQUEST_TARGET_STATUS.READY
      )

      this.requestTargets = this.requestTargets.map(target => {
        if (target.status !== REQUEST_TARGET_STATUS.READY) {
          return target
        }

        return {
          ...target,
          errorMessage: '',
          status: REQUEST_TARGET_STATUS.LOADING,
        }
      })

      const results = await Promise.all(
        readyTargets.map(target => this.submitRequestTarget(target))
      )

      this.requestTargets = this.requestTargets.map(target => {
        const updatedTarget = results.find(
          result => result.accountId === target.accountId
        )

        return updatedTarget || target
      })

      this.isLoading = false

      const hasSuccess = results.some(
        result => result.status === REQUEST_TARGET_STATUS.SUCCESS
      )

      if (hasSuccess) {
        this.$emit('approved')
      }

      if (
        !results.some(
          result => result.status === REQUEST_TARGET_STATUS.ERROR
        )
      ) {
        this.$emit('close')
      }
    },

    async submitRequestTarget(target) {
      try {
        await api.routine.createRoutineApproval(
          this.organizationId,
          this.routineId,
          {
            approver: target.accountId,
          }
        )

        return {
          ...target,
          errorMessage: '',
          status: REQUEST_TARGET_STATUS.SUCCESS,
        }
      } catch (error) {
        return {
          ...target,
          errorMessage:
            error?.response?.data?.message ||
            this.$t('routines.approval.request_creation_failed_for_user', {
              name: this.getTargetName(target),
            }),
          status: REQUEST_TARGET_STATUS.ERROR,
        }
      }
    },
  },
}
</script>
